import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react"
import {
  formatLibraryDate,
  LibraryItemRow,
  LibraryItemTile,
  type LibraryItem,
  type LibraryItemKind,
  type LibraryPickProps,
} from "@/components/library/library-item"
import { filterLibraryItems, useLibraryKindFilter } from "@/components/library/library-kind-filter"
import { SortIcon } from "@/components/ui/data-table"
import { useTranslation } from "@/hooks/use-translation"
import {
  layoutLibraryMasonry,
  libraryAspectRatio,
  LIBRARY_MASONRY_MIN_COLUMN_REM,
  LIBRARY_MASONRY_SPACE_REM,
  LIBRARY_TILE_CAPTION_REM,
} from "@/lib/library-masonry"
import { libraryShiftClick, libraryShiftPlainClick, type LibraryShiftGesture } from "@/lib/library-shift-range"
import {
  LIBRARY_SORT_DEFAULT,
  sortLibraryItems,
  type LibrarySortColumn,
  type LibrarySortState,
} from "@/lib/library-sort"
import type { LibraryViewMode } from "@/lib/library-view-mode"
import { cn } from "@/lib/utils"

/**
 * Отдаёт странице плоский список содержимого папки: по нему работают клавиатура
 * и перелистывание в просмотрщике. Сигнатура эффекта — ключи, а не ссылка на массив.
 */
export function LibraryItemsReporter({
  items,
  onItemsChange,
}: {
  items: LibraryItem[]
  onItemsChange?: (items: LibraryItem[]) => void
}) {
  const itemsRef = useRef(items)
  itemsRef.current = items
  // В сигнатуру входит и подпись: переименование не меняет набор ключей, и без неё
  // страница не узнавала бы о новом имени — в просмотрщике оставалось бы старое.
  const signature = items.map((item) => `${item.key}\u0000${item.label}`).join("|")

  useEffect(() => {
    onItemsChange?.(itemsRef.current)
  }, [signature, onItemsChange])

  return null
}

/**
 * Режим выбора приходит контекстом, а не пропами: узлы папок (шот, сцена, Холст,
 * загрузки) о выборе ничего не знают и остаются общими для страницы и модалки.
 */
export type LibraryPickContextValue = {
  unpickable: (item: LibraryItem) => string | null
  /** Ассет отмечен чекбоксом. */
  selected: (item: LibraryItem) => boolean
  /** Клик по чекбоксу. Папок не касается: они по клику открываются. */
  onToggle: (item: LibraryItem) => void
  /**
   * Shift в мультивыборе: заново отметить кусок и снять строки, которые из него вышли.
   * selectItems идут от якоря к клику. Нет колбэка — Shift как обычный клик.
   */
  onToggleRange?: (selectItems: LibraryItem[], dropItems: LibraryItem[]) => void
  toggleLabel: string
}

const LibraryPickContext = createContext<LibraryPickContextValue | null>(null)

export function LibraryPickProvider({
  value,
  children,
}: {
  value: LibraryPickContextValue | null
  children: ReactNode
}) {
  return <LibraryPickContext.Provider value={value}>{children}</LibraryPickContext.Provider>
}

type LibrarySortContextValue = {
  sort: LibrarySortState
  toggleSort: (column: LibrarySortColumn) => void
}

const LibrarySortContext = createContext<LibrarySortContextValue>({
  sort: LIBRARY_SORT_DEFAULT,
  toggleSort: () => undefined,
})

/** Тот же выбор колонки, что у просмотрщика: сетка, таблица и перелистывание не разъезжаются. */
export function LibrarySortProvider({
  sort,
  onToggle,
  children,
}: {
  sort: LibrarySortState
  onToggle: (column: LibrarySortColumn) => void
  children: ReactNode
}) {
  const value = useMemo(() => ({ sort, toggleSort: onToggle }), [sort, onToggle])
  return <LibrarySortContext.Provider value={value}>{children}</LibrarySortContext.Provider>
}

function useLibrarySort(): LibrarySortContextValue {
  return useContext(LibrarySortContext)
}

/**
 * Куда просят отрисовать действия элемента. Слоты разные не для красоты: карандаш
 * переименования поверх кадра читается как правка изображения (эскиз), поэтому в плитке
 * он живёт на строке подписи, а угол превью остаётся за удалением.
 */
export type LibraryItemActionSlot =
  /** Угол превью в плитке — подложка-чип поверх картинки. */
  | "media"
  /** Строка подписи в плитке — фон сплошной, кнопка без чипа. */
  | "caption"
  /** Ячейка действий в режиме списка: там превью нет, и слот один на все действия. */
  | "row"

type Props = {
  items: LibraryItem[]
  mode: LibraryViewMode
  /** Аспект плиток в режиме сетки: формат кадра проекта для шотов, квадрат для Холста. */
  aspectClass?: string
  /** Ключ элемента под клавиатурным курсором (roving tabindex). */
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  /**
   * Действия над элементом (переименовать, удалить) — только у секций, где они есть.
   * Вызывается по разу на слот, и вернуть надо ровно то, чему в этом слоте место.
   * В режиме выбора не рисуется: угол плитки занят чекбоксом.
   */
  itemActions?: (item: LibraryItem, slot: LibraryItemActionSlot) => ReactNode
  /**
   * table — шапка и колонки. Дата и время в одной ячейке.
   * rows — строки без шапки.
   */
  listLayout?: "rows" | "table"
  /** Вторая часть одного списка (загрузки под генерациями) не повторяет шапку. */
  showHeader?: boolean
}

/** Тип, хронометраж и дата — отдельные колонки: на узкой ширине остаются название и тип. */
// Последняя колонка — под действия строки. 3.5rem, а не 2rem: у секций с двумя иконками
// (переименовать + удалить) пара не влезала в ширину одной кнопки и наезжала на дату.
const ROW_GRID =
  "grid grid-cols-[minmax(0,1fr)_5rem_3.5rem] gap-3 sm:grid-cols-[minmax(0,1fr)_6.5rem_4.5rem_7.5rem_3.5rem]"
const ROW_GRID_PICK =
  "grid grid-cols-[1.25rem_minmax(0,1fr)_5rem_3.5rem] gap-3 sm:grid-cols-[1.25rem_minmax(0,1fr)_6.5rem_4.5rem_7.5rem_3.5rem]"

// Таблица не прячет колонки на узкой ширине: контейнер библиотеки уже скроллится по горизонтали
// (overflow-y-auto вычисляет overflow-x: auto). Ширины хватает на «Хронометраж» и «Изображение».
const TABLE_GRID = "grid grid-cols-[minmax(12rem,1fr)_7rem_7.5rem_7.5rem_3.5rem] gap-3"
const TABLE_GRID_PICK = "grid grid-cols-[1.25rem_minmax(12rem,1fr)_7rem_7.5rem_7.5rem_3.5rem] gap-3"

/** Пока ширина не измерена — прежняя сетка, чтобы блок не схлопывался до первого кадра. */
const TILE_GRID_CLASS = "grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] items-start gap-3 p-3"

type TileGridProps = {
  items: LibraryItem[]
  aspectClass: string
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpen: (item: LibraryItem) => void
  itemActions?: (item: LibraryItem, slot: LibraryItemActionSlot) => ReactNode
  currentLabel: string
  pickProps: (item: LibraryItem) => LibraryPickProps
}

/**
 * Плитки колонками: короткая карточка не растягивает ряд. DOM-порядок — порядок
 * списка, карточки стоят absolute, чтобы стрелки влево/вправо шли слева направо.
 */
function LibraryTileGrid({
  items,
  aspectClass,
  focusedKey,
  onFocusItem,
  onOpen,
  itemActions,
  currentLabel,
  pickProps,
}: TileGridProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    const node = containerRef.current
    if (!node) return
    const update = () => {
      const next = node.getBoundingClientRect().width
      setContainerWidth((prev) => (prev != null && Math.abs(prev - next) < 0.5 ? prev : next))
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const layout = useMemo(() => {
    if (containerWidth == null || containerWidth <= 0) return null
    const fontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    return layoutLibraryMasonry({
      count: items.length,
      containerWidth,
      minColumnWidth: LIBRARY_MASONRY_MIN_COLUMN_REM * fontSize,
      gap: LIBRARY_MASONRY_SPACE_REM * fontSize,
      padding: LIBRARY_MASONRY_SPACE_REM * fontSize,
      captionPx: LIBRARY_TILE_CAPTION_REM * fontSize,
      aspectRatio: (index) => libraryAspectRatio(items[index]?.aspectClass ?? aspectClass),
    })
  }, [aspectClass, containerWidth, items])

  const tile = (item: LibraryItem) => (
    <LibraryItemTile
      item={item}
      aspectClass={item.aspectClass ?? aspectClass}
      currentLabel={currentLabel}
      focused={focusedKey === item.key}
      onOpen={() => onOpen(item)}
      onFocus={() => onFocusItem?.(item.key)}
      actions={itemActions?.(item, "media")}
      labelAction={itemActions?.(item, "caption")}
      {...pickProps(item)}
    />
  )

  const ready = layout != null && layout.placements.length === items.length
  if (!ready || !layout) {
    return (
      <div ref={containerRef} className={TILE_GRID_CLASS}>
        {items.map((item) => (
          <div key={item.key} className="contents">
            {tile(item)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="relative w-full" style={{ height: layout.height }}>
      {items.map((item, index) => {
        const place = layout.placements[index]
        if (!place) return null
        return (
          <div key={item.key} className="absolute" style={{ top: place.top, left: place.left, width: place.width }}>
            {tile(item)}
          </div>
        )
      })}
    </div>
  )
}

/** Содержимое папки одним компонентом на оба режима: сетка плиток или таблица. */
export function LibraryItems({
  items: allItems,
  mode,
  aspectClass = "aspect-video",
  focusedKey,
  onFocusItem,
  onOpenItem,
  itemActions,
  listLayout = "rows",
  showHeader = true,
}: Props) {
  const { t, locale } = useTranslation(["workspace", "common"])
  const pick = useContext(LibraryPickContext)
  const { sort, toggleSort } = useLibrarySort()
  // Фильтр по типу применяем ровно здесь, на отрисовке: узлы папок отдают полный
  // список и о фильтре не знают, а счётчики чипов обязаны считаться по всей папке —
  // иначе включённое «Видео» обнуляло бы соседние вкладки и снять его было бы нечем.
  const kindFilter = useLibraryKindFilter()
  const typeLabel = useMemo(
    (): Record<LibraryItemKind, string> => ({
      folder: t("library.types.folder"),
      image: t("library.types.image"),
      video: t("library.types.video"),
      audio: t("library.types.audio"),
    }),
    [t],
  )
  const items = useMemo(
    () => sortLibraryItems(filterLibraryItems(allItems, kindFilter), sort, { locale, typeLabel }),
    [allItems, kindFilter, sort, locale, typeLabel],
  )
  // Якорь и последний Shift-кусок. Сбрасываются вместе с папкой: список размонтируется.
  const gestureRef = useRef<LibraryShiftGesture>({ anchor: null, span: [] })
  if (items.length === 0) return null

  const open = (item: LibraryItem) => {
    if (onOpenItem) onOpenItem(item)
    else item.onOpen?.()
  }

  const togglePicked = (item: LibraryItem, shiftKey: boolean) => {
    if (!pick) return
    if (shiftKey && pick.onToggleRange) {
      const assets = items.filter((row) => row.kind !== "folder")
      const anchorKey = gestureRef.current.anchor
      const anchor = anchorKey == null ? undefined : assets.find((row) => row.key === anchorKey)
      // Снятый якорь больше не точка отсчёта. Вне видимого списка Shift тоже молчит,
      // но якорь не забываем: фильтр могут снять.
      if (!anchor || !pick.selected(anchor)) {
        if (anchor && !pick.selected(anchor)) gestureRef.current = { anchor: null, span: [] }
        return
      }
      const next = libraryShiftClick(assets, gestureRef.current, item.key)
      if (!next) return
      const byKey = new Map(allItems.map((row) => [row.key, row]))
      const dropItems = next.dropKeys.flatMap((key) => {
        const row = byKey.get(key)
        return row ? [row] : []
      })
      gestureRef.current = next.gesture
      pick.onToggleRange(next.select, dropItems)
      return
    }
    const wasSelected = pick.selected(item)
    // unpickable покрывает и лимит: клик ничего не включит, якорь не переезжает.
    const willSelect = !wasSelected && item.source != null && pick.unpickable(item) == null
    pick.onToggle(item)
    if (wasSelected) gestureRef.current = libraryShiftPlainClick(gestureRef.current, item.key, false)
    else if (willSelect) gestureRef.current = libraryShiftPlainClick(gestureRef.current, item.key, true)
  }

  const pickProps = (item: LibraryItem) =>
    pick
      ? {
          picking: true,
          unpickable: pick.unpickable(item),
          selected: pick.selected(item),
          onToggle: (shiftKey: boolean) => togglePicked(item, shiftKey),
          toggleLabel: pick.toggleLabel,
        }
      : {}

  const currentLabel = t("library.columns.current")

  if (mode === "list") {
    const table = listLayout === "table"
    const grid = table ? (pick ? TABLE_GRID_PICK : TABLE_GRID) : pick ? ROW_GRID_PICK : ROW_GRID
    // Сетку не отдаём в cn(): в произвольном grid-cols есть запятая, и tailwind-merge её режет.
    const rowClass = `${grid} ${cn(
      "h-11 items-center border-b border-ui-border/60 px-2 last:border-b-0 hover:bg-ui-surface-raised",
      !table && "rounded-md",
    )}`
    const headerClass = `${grid} ${cn(
      "sticky top-0 z-10 items-center border-b border-ui-border bg-ui-bg-elevated px-2 py-1.5 text-[11px] whitespace-nowrap text-ui-text-muted",
    )}`
    return (
      <div className={table ? (pick ? "min-w-[46rem]" : "min-w-[44rem]") : undefined}>
        {table && showHeader ? (
          <div className={headerClass}>
            {pick ? <span /> : null}
            <SortHead column="name" label={t("library.columns.name")} active={sort} onToggle={toggleSort} />
            <SortHead column="type" label={t("library.columns.type")} active={sort} onToggle={toggleSort} />
            <SortHead
              column="duration"
              label={t("library.columns.duration")}
              align="right"
              active={sort}
              onToggle={toggleSort}
            />
            <SortHead
              column="date"
              label={t("library.columns.date")}
              align="right"
              active={sort}
              onToggle={toggleSort}
            />
            <span />
          </div>
        ) : null}
        {items.map((item) => (
          <LibraryItemRow
            key={item.key}
            item={item}
            typeLabel={typeLabel[item.kind]}
            dateLabel={formatLibraryDate(item.createdAt, locale)}
            layout={table ? "table" : "compact"}
            currentLabel={currentLabel}
            rowClass={rowClass}
            focused={focusedKey === item.key}
            onOpen={() => open(item)}
            onFocus={() => onFocusItem?.(item.key)}
            actions={itemActions?.(item, "row")}
            {...pickProps(item)}
          />
        ))}
      </div>
    )
  }

  return (
    <LibraryTileGrid
      items={items}
      aspectClass={aspectClass}
      focusedKey={focusedKey}
      onFocusItem={onFocusItem}
      onOpen={open}
      itemActions={itemActions}
      currentLabel={currentLabel}
      pickProps={pickProps}
    />
  )
}

/** Шапка колонки: первое включение — по убыванию, повторный клик меняет направление. */
function SortHead({
  column,
  label,
  align = "left",
  active,
  onToggle,
}: {
  column: LibrarySortColumn
  label: string
  align?: "left" | "right"
  active: LibrarySortState
  onToggle: (column: LibrarySortColumn) => void
}) {
  const selected = active.id === column
  return (
    <button
      type="button"
      className={cn(
        "group/head inline-flex min-w-0 items-center gap-1.5 transition-colors hover:text-ui-text-primary",
        align === "right" && "flex-row-reverse justify-self-end",
      )}
      onClick={() => onToggle(column)}
    >
      <span className="truncate">{label}</span>
      <SortIcon active={selected} desc={selected && active.desc} />
    </button>
  )
}
